import { useState, useEffect, useContext } from "react"
import { useUserContext } from "./RetrieveImageContext"
import axios from "axios"
import GetCSFR from "../userAuthentication/GetCSFR";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlay } from "@fortawesome/free-regular-svg-icons";

interface Image {
    id?: string;
    url?: string;
    styling: string;
    position?: string
    disableOnClick?: boolean;
    storeMedia?: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function RetrieveImage({ styling, url, id, position, disableOnClick, storeMedia}:Image) {
  const [imageUrl, setImageUrl] = useState("")
  const csrfToken = GetCSFR({ name: "csrftoken" })
  const userContext = useUserContext()
  const [previewMedia, setPreviewMedia] = useState<string | null>(null)
  
    useEffect(() => {
        const fetchCacheImage = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_SKILLJA_URL}/image/get_cached_image/`, {
                    headers: {
                        'X-CSRFToken': csrfToken,
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                })
                // if coach image is not requested and cached image exists
                if (res.status === 200 && !id && !url) {
                    setImageUrl(res.data.profile_image_url)
                    // updated storeMedia with profile picture
                    if(imageUrl && storeMedia){
                        storeMedia((prev: string[]) => {
                        const next = [...prev];
                        next[0] = res.data.profile_image_url;
                        return next;
                    });
                    }
                } 
                else if (res.status === 204 || id || url) {
                    // If no cached image, fetch a new profile image url or fetch coach image
                    const fetchImage = async (endpoint:string) => {
                        try {
                            const response = await axios.get(endpoint, { 
                                headers: {
                                    'X-CSRFToken': csrfToken,
                                    'Content-Type': 'application/json'
                                },
                                withCredentials: true
                            })
                            if (response.status === 200) {
                                setImageUrl(response.data.signed_url)
                            
                                // upload image url if requested for edit profile functionality
                                if(storeMedia && position){
                                    const positions = ['one','two','three','four','club','video']
                                    const index = positions.indexOf(position.toLowerCase());

                                    // update other available indexes in storedMedia
                                    if (index !== -1) {
                                        storeMedia((prev: string[]) => {
                                            const next = [...prev];
                                            next[index] = response.data.signed_url;
                                            return next;
                                        });
                                    }
                                }
                            } 
                        } catch (error) {
                            // console.error("Could not retrieve the image.")
                        }
                    } 
                    // Construct the appropriate endpoint based on input parameters
                    if (userContext.imageName !=="" && userContext.imageName !== "default") {
                        if (position?.includes('video')) {
                            const endpoint = `${process.env.REACT_APP_SKILLJA_URL}/image/get_video/?id=${parseInt(id!)}&position=${position}`
                            fetchImage(endpoint)
                        }
                        else {
                            const endpoint = `${process.env.REACT_APP_SKILLJA_URL}/image/get_image/?image_name=${userContext.imageName}&cache=${userContext.cache}`
                            fetchImage(endpoint)
                        }
                    } 
                    else if (userContext.id) {
                        const endpoint = `${process.env.REACT_APP_SKILLJA_URL}/image/get_image/?id=${userContext.id}`
                        fetchImage(endpoint)
                    }   
                    else if (id) {
                        if(position){
                            const endpoint = `${process.env.REACT_APP_SKILLJA_URL}/image/get_image/?id=${id}&position=${position}`
                            fetchImage(endpoint)
                        }
                        else{
                            const endpoint = `${process.env.REACT_APP_SKILLJA_URL}/image/get_image/?id=${id}`
                            fetchImage(endpoint)
                        }
                    } 
                    else if (url) {
                        setImageUrl(url)
                    }
                }
            } catch (error) {
                console.error("Error checking authentication", error)
            }
        }   
        
        fetchCacheImage()
    }, [])

    // update state of storedMedia for parent component
    useEffect(() => {
        if (!imageUrl || !storeMedia || !position) return;

        const positions = ['one','two','three','four','club','video'];
        const index = positions.indexOf(position.toLowerCase());

        if (index === -1) return;

        storeMedia(prev => {
            const next = [...prev];
            next[index] = imageUrl;
            return next;
        });
    }, [imageUrl, position, storeMedia, id]);


  return (
    <>
        {position && position.includes('video') && imageUrl ?
            <div className="relative">
                <FontAwesomeIcon icon={faCirclePlay} className="absolute text-main-grey-200 text-4xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"/>
                <video 
                    src={imageUrl}
                    className={`${styling? styling: "w-60 h-60"} cursor-pointer`}
                    onClick={() => {
                        if (disableOnClick) return;
                        setPreviewMedia(imageUrl ? imageUrl : (position && position.includes('video') ? require("../../assets/default-video-avatar.png") : require("../../assets/default-avatar.jpg")) );
                    }}
                />
            </div>
        :   
            // do not render anything if a club image is requested but does not exist, otherwise render image/default image
            (position && position.includes('club') && !imageUrl ?
                <></>
            :
                <img 
                    src={imageUrl ? imageUrl : require('../../assets/default-avatar.jpg')} 
                    alt={imageUrl ? "logo of club/employer": "default avatar"}
                    className={`${styling? styling: "w-60 h-60"} cursor-pointer`}
                    onClick={() => {
                        if (disableOnClick) return;
                        setPreviewMedia(imageUrl ? imageUrl : require("../../assets/default-avatar.jpg") );
                    }}
                />
            )
        } 
        {previewMedia && position && !position.includes('video') ?
            <div 
                className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
                onClick={() => setPreviewMedia(null)} // click anywhere to close
            >
                <div className="max-w-2xl max-h-[70vh] p-4">
                    <img 
                        src={previewMedia}
                        className="rounded-2xl object-cover w-72 h-72 mx-auto"
                        onContextMenu={(e) => e.preventDefault()}
                    />
                </div>
            </div>
            :
            (previewMedia &&
                <div 
                    className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
                    onClick={() => setPreviewMedia(null)} // click anywhere to close
                >
                    <div className="max-w-2xl max-h-[70vh] p-4">
                        <video 
                            src={previewMedia}
                            className="rounded-2xl max-w-full max-h-[70vh]"
                            onClick={() => {
                                if (disableOnClick) return;
                                setPreviewMedia(imageUrl);
                            }}
                            controls
                        />
                    </div>
                </div>
            )
        }
    </>
  )
}
